/**
 * Shared params for the current onboarding step: flow name, step index, and resolved selector.
 * Used by useOnboardingQueryParams, useStepValidationMap, and useValidationObserver.
 */
export type OnboardingStepParams = {
  onboardingFlow: string;
  stepIndex: number;
  /** Resolved step selector, or undefined when the step has no target (center-of-screen). */
  selector: string | undefined;
};

/**
 * Represents a single step in an onboarding guidance flow.
 *
 * @property {string} title - The title of the step
 * @property {string} description - A brief description explaining the purpose of the step
 * @property {string} [selector] - The selector for the step, combined with the flow name as data-guidance-step="flowName:selector". When omitted or undefined, the step binds to the center of the screen (no target element; popover shows centered with no arrow and no Back/Continue/Done).
 * @property {Object<{ type: 'video' | 'image'; path: string; alt?: string }>} [media] - Optional media object to be displayed alongside the step
 *
 * @example
 * const step: OnboardingGuidanceStep = {
 *   title: 'Install SDK',
 *   description: 'Install the LaunchDarkly SDK in your application',
 *   selector: 'install-sdk',
 *   media: {
 *     type: 'video',
 *     path: '/assets/sdk-install.gif',
 *     alt: 'Install SDK',
 *   },
 * };
 */
export type OnboardingGuidanceStep = {
  title: string;
  description: string;
  /**
   * Selector for the step (combined with flow name as data-guidance-step="flowName:selector").
   * When omitted or undefined, the step binds to the center of the screen: no target element is
   * used, and the popover is shown centered with no arrow and no Back/Continue/Done buttons.
   */
  selector?: string;
  media?: {
    type: 'video' | 'image';
    path: string;
    alt?: string;
  };
};

/**
 * The group/category a flow belongs to.
 * Used to filter flows in the setup guide UI.
 */
export enum OnboardingFlowGroup {
  FeatureFlags = 'feature-flags',
  AiConfigs = 'ai-configs',
  Experimentation = 'experimentation',
  Observability = 'observability',
}

/**
 * Base type for an onboarding guidance flow.
 * Use this for static flow definitions that don't need onClick handlers.
 * Flows with onClick handlers should use OnboardingGuidanceFlowWithHandler.
 *
 * @property {string} name - The unique name of the onboarding flow
 * @property {string} description - A brief description explaining the purpose of this onboarding flow
 * @property {OnboardingGuidanceStep[]} steps - An ordered array of steps that make up the onboarding flow
 * @property {OnboardingFlowGroup} group - The category this flow belongs to (feature-flags, ai-configs, experimentation, observability)
 *
 * @example
 * const flow: OnboardingGuidanceFlow = {
 *   name: 'Getting Started',
 *   description: 'Learn the basics of feature flagging',
 *   steps: [stepOne, stepTwo, stepThree],
 *   group: OnboardingFlowGroup.FeatureFlags,
 * };
 */
export type OnboardingGuidanceFlow = {
  name: string;
  description: string;
  steps: OnboardingGuidanceStep[];
  group: OnboardingFlowGroup;
};

/**
 * Extended type for onboarding guidance flows that include an onClick handler.
 * Use this for flows that need dynamic onClick handlers (typically provided by hooks).
 *
 * @property {Function} onClick - Function to be invoked when selecting the Onboarding Setup Flow. Can be used to trigger side effects or initialization logic for the flow
 * @property {boolean} completed - Indicates whether the onboarding flow has been completed
 *
 * @example
 * const flow: OnboardingGuidanceFlowWithHandler = {
 *   name: 'Getting Started',
 *   description: 'Learn the basics of feature flagging',
 *   steps: [stepOne, stepTwo, stepThree],
 *   onClick: () => initializeFlow(),
 *   completed: false
 * };
 */
export type OnboardingGuidanceFlowWithHandler = OnboardingGuidanceFlow & {
  completed: boolean;
  onClick: () => void;
};

export type OnboardingGuidanceModalType = 'setup-guide' | 'setup-drawer' | 'off';
